import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, Loader2, Lock, Minus, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useYouTubeAccountReset } from '@/lib/youtubeConnection';
import { formatApiError } from '@/lib/formatApiError';
import { ChannelSectionNav } from '@/components/channel/ChannelSectionNav';

const LOCKED_PHASES = [
  {
    id: 'compliance',
    label: 'Compliance',
    steps: [
      { id: 'audience', label: 'Made for Kids is answered', why: 'The wrong answer disables comments, cards, and end screens.' },
      { id: 'disclosures', label: 'Paid promotion and AI disclosure are answered', why: 'Sponsored or synthetic video needs an explicit disclosure.' },
      { id: 'rights_wait', label: 'Wait for YouTube copyright and ad checks', why: 'Stay unlisted until YouTube finishes Checks.' },
    ],
  },
  {
    id: 'seo',
    label: 'Advanced SEO',
    steps: [
      { id: 'title_package', label: 'Title length, phrase placement, and thumbnail contrast', why: 'The title and thumbnail must complement each other.' },
      { id: 'thumbnail', label: 'Custom thumbnail specs', why: '1280×720, under 2MB, three overlay words, timestamp zone clear.' },
      { id: 'description_chapters', label: 'Description, question chapters, and hashtags', why: 'The first 150 characters and chapter names are what search can quote.' },
      { id: 'tags', label: 'Primary phrase first, 5–15 tags', why: 'Stuffing tags lowers reach.' },
      { id: 'captions', label: 'Language and a corrected SRT', why: 'Auto-captions misspell the terms you want indexed.' },
      { id: 'spoken_cue', label: 'Phrase spoken in the first minute', why: 'YouTube transcribes the opening and uses it as context.' },
    ],
  },
  {
    id: 'distribution',
    label: 'Pre-publish actions',
    steps: [
      { id: 'routing', label: 'Category, language, license, embedding', why: 'These route the video to the right audience.' },
      { id: 'playlist', label: 'Playlist for a public video', why: 'Playlists extend the session.' },
      { id: 'schedule', label: 'Unlisted until HD is ready', why: 'Instant public sends early viewers a soft picture.' },
      { id: 'pinned_comment', label: 'Pinned comment draft', why: 'Pin it in Studio when the video goes public.' },
      { id: 'session_routing', label: 'End screen and cards', why: 'Studio-only. Point viewers to the next video.' },
    ],
  },
  {
    id: 'ranking',
    label: 'Ranking on YouTube',
    steps: [
      { id: 'ctr', label: 'CTR versus the channel baseline', why: 'Weak CTR with impressions means the package needs a revision.' },
      { id: 'hook_retention', label: 'Viewers still watching at 30 seconds', why: 'An early drop means the opening or the promise missed.' },
      { id: 'traffic_mix', label: 'Search, Suggested, and Browse mix', why: 'Search problems are metadata. Browse problems are the thumbnail.' },
      { id: 'watch_time', label: 'Watch time over raw CTR', why: 'Keep the version people finish, even if another title gets more clicks.' },
    ],
  },
];

function statusMark(status) {
  if (status === 'pass') return { icon: Check, className: 'bg-[#34d399] text-[#06211b] border-[#34d399]' };
  if (status === 'action') return { icon: AlertCircle, className: 'bg-amber-300 text-[#1a1204] border-amber-300' };
  if (status === 'fail') return { icon: X, className: 'bg-rose-400 text-[#2a0b12] border-rose-400' };
  if (status === 'skipped') return { icon: Minus, className: 'bg-white/10 text-[#8b95a7] border-white/15' };
  if (status === 'studio_only') return { icon: Minus, className: 'bg-amber-300/20 text-amber-100 border-amber-200/40' };
  return { icon: Lock, className: 'bg-black/40 text-[#d6ff4a] border-[#d6ff4a]/40' };
}

function YesNo({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span>{label}</span>
      <span className="flex gap-2">
        <button type="button" onClick={() => onChange(true)} className={`rounded-full px-3 py-1 cursor-pointer ${value === true ? 'bg-[#34d399] text-[#06211b]' : 'bg-black/30'}`}>Yes</button>
        <button type="button" onClick={() => onChange(false)} className={`rounded-full px-3 py-1 cursor-pointer ${value === false ? 'bg-[#34d399] text-[#06211b]' : 'bg-black/30'}`}>No</button>
      </span>
    </div>
  );
}

function Timeline({ phases, locked, onFix }) {
  return (
    <div className="space-y-8">
      {phases.map((phase) => (
        <section key={phase.id} className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8b95a7]">{phase.label}</h2>
          <ol className="relative space-y-2 border-l border-white/10 ml-3 pl-5">
            {(phase.steps || []).map((step) => {
              const mark = locked ? statusMark('locked') : statusMark(step.status);
              const Icon = mark.icon;
              return (
                <li key={step.id} className="relative rounded-2xl bg-[#141a24] px-4 py-3">
                  <span className={`absolute -left-[29px] top-4 flex h-5 w-5 items-center justify-center rounded-full border ${mark.className}`}>
                    <Icon className="w-3 h-3" strokeWidth={3} />
                  </span>
                  <p className="text-sm font-medium text-white">{step.label}</p>
                  {locked ? (
                    <p className="text-xs text-[#8b95a7] mt-1">{step.why}</p>
                  ) : (
                    <>
                      {step.detail ? <p className="text-xs text-[#d7f8ec] mt-1">{step.detail}</p> : null}
                      {step.action ? <p className="text-xs text-amber-100 mt-1">{step.action}</p> : null}
                      {step.tip ? <p className="text-xs text-[#8b95a7] mt-1">{step.tip}</p> : null}
                      {step.status ? (
                        <p className="text-[11px] uppercase tracking-wide text-[#6d7788] mt-2">{step.status.replace('_', ' ')}</p>
                      ) : null}
                      {onFix && (step.status === 'fail' || step.status === 'action') ? (
                        <button
                          type="button"
                          onClick={onFix}
                          className="mt-2 rounded-full bg-amber-300 px-3 py-1 text-xs font-bold text-[#1a1204] cursor-pointer"
                        >
                          Fix it
                        </button>
                      ) : null}
                    </>
                  )}
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}

export function ChannelAdvancedPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const videoId = params.get('video') || '';
  const [locked, setLocked] = useState(false);
  const [report, setReport] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [madeForKids, setMadeForKids] = useState(null);
  const [paidPromotion, setPaidPromotion] = useState(null);
  const [alteredContent, setAlteredContent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/channel/advanced', {
        params: videoId ? { video_id: videoId } : {},
      });
      setLocked(false);
      setReport(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 || detail?.unlock_required) {
        setLocked(true);
        setReport(null);
      } else {
        setLocked(false);
        setReport(null);
      }
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (locked) return undefined;
    let cancelled = false;
    api.get('/channel/videos', { params: { limit: 20 } })
      .then(({ data }) => {
        if (!cancelled) setVideos(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (!cancelled) setVideos([]);
      });
    return () => {
      cancelled = true;
    };
  }, [locked]);

  const clearChannel = useCallback(() => {
    setVideos([]);
    setReport(null);
    setDraft(null);
  }, []);
  useYouTubeAccountReset(clearChannel);

  const legalReady = madeForKids !== null && paidPromotion !== null && alteredContent !== null;

  const startFix = useCallback(async () => {
    if (!videoId) return;
    setFixing(true);
    try {
      const [{ data: videoPayload }, { data: improved }] = await Promise.all([
        api.get(`/channel/videos/${videoId}`),
        api.post(`/channel/videos/${videoId}/improve-seo`),
      ]);
      const video = videoPayload?.video || {};
      const next = {
        title: improved?.title || video.title || '',
        description: typeof improved?.description === 'string' ? improved.description : (video.description || ''),
        tags: Array.isArray(improved?.tags) ? improved.tags : (video.tags || []),
        language: improved?.default_language || video.default_language || '',
        privacy: video.privacy_status || 'public',
        categoryId: video.category_id || '22',
        hasThumbnail: Boolean(video.has_custom_thumbnail),
        shorts: Boolean(video.is_short),
        strategy: improved?.strategy || '',
        pinned: '',
        thumbnailText: '',
      };
      const { data: audit } = await api.post('/channel/prepublish-audit', {
        title: next.title,
        description: next.description,
        tags: next.tags,
        default_language: next.language,
        category_id: next.categoryId,
        privacy_status: next.privacy,
        has_custom_thumbnail: next.hasThumbnail,
        optimize_for_shorts: next.shorts,
        in_playlist: Boolean(video.in_playlist),
      });
      next.pinned = audit?.pinned_comment_suggestion || '';
      next.thumbnailText = audit?.thumbnail_text_suggestion || '';
      setDraft(next);
      setMadeForKids(null);
      setPaidPromotion(null);
      setAlteredContent(null);
      if (improved?.error) toast.error(improved.error);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not prepare a fix'));
    } finally {
      setFixing(false);
    }
  }, [videoId]);

  const publishFix = async () => {
    if (!draft || !legalReady) {
      toast.error('Answer the three questions before saving.');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/channel/videos/${videoId}`, {
        title: draft.title.trim(),
        description: draft.description,
        tags: draft.tags,
        privacy_status: draft.privacy,
        category_id: draft.categoryId,
        default_language: draft.language || null,
        made_for_kids: madeForKids,
        paid_promotion: paidPromotion,
        altered_content: alteredContent,
        pinned_comment: draft.pinned,
      });
      toast.success('Saved to YouTube. Pin the comment and end screen in Studio.');
      setDraft(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save to YouTube'));
    } finally {
      setSaving(false);
    }
  };

  const phases = locked ? LOCKED_PHASES : report?.phases || [];

  return (
    <main className="p-4 sm:p-8 font-jakarta overflow-x-hidden text-white space-y-6" data-testid="channel-advanced-page">
      <ChannelSectionNav />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Advanced analytics</h1>
          <p className="text-sm text-[#8b95a7] mt-1">
            Compliance, packaging, pre-publish actions, and ranking. One timeline, no repeated checks.
          </p>
        </div>
        {locked ? (
          <button
            type="button"
            onClick={() => navigate('/pricing?unlock=analytics')}
            className="inline-flex items-center gap-2 rounded-full bg-amber-300 px-4 py-2 text-sm font-bold text-[#1a1204] cursor-pointer"
            data-testid="advanced-unlock-cta"
          >
            <Lock className="w-4 h-4" />
            Unlock
          </button>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            {videoId ? (
              <button
                type="button"
                onClick={startFix}
                disabled={fixing}
                className="inline-flex items-center gap-2 rounded-full bg-amber-300 px-4 py-2 text-sm font-bold text-[#1a1204] cursor-pointer disabled:opacity-50"
                data-testid="advanced-fix-it"
              >
                {fixing ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
                {fixing ? 'Preparing the fix…' : 'Fix it'}
              </button>
            ) : null}
          <label className="text-xs text-[#8b95a7]">
            Video
            <select
              value={videoId}
              onChange={(event) => {
                const next = event.target.value;
                setParams(next ? { video: next } : {});
              }}
              className="mt-1 block rounded-xl bg-[#141a24] border border-white/10 px-3 py-2 text-sm text-white"
              data-testid="advanced-video-select"
            >
              <option value="">Choose a published video</option>
              {videos.map((video) => (
                <option key={video.video_id} value={video.video_id}>
                  {video.title || video.video_id}
                </option>
              ))}
            </select>
          </label>
          </div>
        )}
      </div>
      {loading ? <p className="text-sm text-[#8b95a7]">Loading the timeline…</p> : null}
      {!loading && locked ? (
        <p className="text-sm text-amber-100/80">Each step stays locked until a plan payment is completed.</p>
      ) : null}
      {!loading && !locked && !report ? (
        <p className="text-sm text-[#d6ff4a]">The timeline could not be loaded. Reconnect YouTube if this video is missing from the catalog.</p>
      ) : null}
      {!loading && !locked && videoId && report?.title ? (
        <p className="text-sm text-white">
          {report.title}{' '}
          <Link className="text-[#34d399] underline" to={`/channel/videos/${videoId}`}>
            Open in video editor
          </Link>
        </p>
      ) : null}
      {draft ? (
        <section className="rounded-[22px] border border-amber-300/30 bg-[#1a160d] p-5 space-y-4 max-w-3xl" data-testid="advanced-fix-confirm">
          <div>
            <h2 className="text-lg font-bold">Check this fix before it goes on YouTube</h2>
            <p className="text-sm text-[#c5cedb] mt-1">
              Nothing is saved yet. The video stays {draft.privacy}. End screens, cards, and captions still need YouTube Studio.
            </p>
            {draft.strategy ? <p className="text-xs text-[#8b95a7] mt-1">{draft.strategy}</p> : null}
          </div>
          <label className="block text-xs text-[#8b95a7]">
            Title
            <input
              value={draft.title}
              onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              className="mt-1 w-full rounded-xl bg-black/30 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="block text-xs text-[#8b95a7]">
            Description
            <textarea
              value={draft.description}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              rows={5}
              className="mt-1 w-full rounded-xl bg-black/30 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="block text-xs text-[#8b95a7]">
            Tags
            <input
              value={draft.tags.join(', ')}
              onChange={(event) => setDraft({
                ...draft,
                tags: event.target.value.split(',').map((tag) => tag.trim()).filter(Boolean),
              })}
              className="mt-1 w-full rounded-xl bg-black/30 px-3 py-2 text-sm text-white"
            />
          </label>
          {draft.thumbnailText ? (
            <p className="text-sm text-amber-100">Put these words on the thumbnail: {draft.thumbnailText}</p>
          ) : null}
          <label className="block text-xs text-[#8b95a7]">
            Comment to pin in Studio
            <textarea
              value={draft.pinned}
              onChange={(event) => setDraft({ ...draft, pinned: event.target.value })}
              rows={2}
              className="mt-1 w-full rounded-xl bg-black/30 px-3 py-2 text-sm text-white"
            />
          </label>
          <YesNo label="Made for kids?" value={madeForKids} onChange={setMadeForKids} />
          <YesNo label="Paid promotion?" value={paidPromotion} onChange={setPaidPromotion} />
          <YesNo label="Altered or synthetic AI?" value={alteredContent} onChange={setAlteredContent} />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={publishFix}
              disabled={saving || !legalReady}
              className="rounded-full bg-[#34d399] px-4 py-2 text-sm font-bold text-[#06211b] cursor-pointer disabled:opacity-40"
              data-testid="advanced-fix-publish"
            >
              {saving ? 'Saving…' : 'Save to YouTube'}
            </button>
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="rounded-full border border-white/15 px-4 py-2 text-sm cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </section>
      ) : null}
      {!loading ? <Timeline phases={phases} locked={locked} onFix={!locked && videoId ? startFix : null} /> : null}
    </main>
  );
}
